import { renderApplication } from './app-view.js';
import { createRuntimeAuthProvider } from './auth/runtime-provider.js';
import { LocalDashboardDataProvider } from './dashboard-provider.js';
import { createDashboardPendingModel } from './loading-data.js';
import type { NotificationGroupInput } from '../settings/model.js';
import type { SettingsPageModel } from './settings/model.js';
import { LocalSettingsDataProvider, SettingsRequestError } from './settings/provider.js';
import {
  allowGroupAssignmentSwitch,
  clearAssignmentSelection,
  createGroupAssignmentEditorState,
  filteredAssignmentEndpoints,
  hasUnsavedGroupAssignmentChanges,
  loadGroupAssignment,
  selectAllFilteredEndpoints,
  selectedAssignmentEndpoints,
  toggleAssignmentEndpoint,
  withAssignmentSearch,
} from './settings/group-assignment.js';
import {
  LocalAuthorizationProfileProvider,
  type AuthorizationProfile,
} from './auth/profile-provider.js';
import type { AuthenticatedUser, AuthState } from './auth/model.js';
import { can } from '../authorization/policy.js';
import { PlatformAdministrationProvider } from './platform-admin/provider.js';
import { LocalClientProvider, ClientRequestError } from './clients/provider.js';
import { ClientOnboardingProvider, ClientOnboardingRequestError } from './clients/onboarding-provider.js';
import { blankOnboardingRow } from './clients/onboarding-view.js';
import { profileValidationError } from './clients/profile-validation.js';
import { OnboardingOperationsProvider } from './onboarding/provider.js';
import type { OnboardingRequestDetail, OnboardingRequestListItem } from '../onboarding/request.js';
import type { ClientOnboardingConfiguration, ProfileInput } from '../onboarding/model.js';
import { DEFAULT_HARDWARE, EMPTY_ACCESS } from '../onboarding/model.js';
import { slugFromName, type ClientDirectory, type CreateClientInput } from '../clients/model.js';
import type { PlatformAdministrationModel } from './platform-admin/model.js';
import {
  isSettingsSectionTransition,
  parseFrontendRoute,
  defaultRoutePath,
  type FrontendRoute,
  type SettingsSectionId,
} from './settings/routing.js';

const root = document.querySelector<HTMLElement>('[data-dashboard-root]');
if (root === null) throw new Error('Dashboard root element is missing');

const authProvider = createRuntimeAuthProvider(window.__MSP_HEALTH_CONFIG__ ?? {});
const dashboardProvider = new LocalDashboardDataProvider();
const settingsProvider = new LocalSettingsDataProvider();
const authorizationProfileProvider = new LocalAuthorizationProfileProvider();
const platformAdministrationProvider = new PlatformAdministrationProvider();
const clientProvider = new LocalClientProvider();
const onboardingProvider = new ClientOnboardingProvider();
const onboardingOperationsProvider = new OnboardingOperationsProvider();
let dashboardModel = createDashboardPendingModel('Waiting for authenticated local data');
let settingsModel: SettingsPageModel | undefined;
let settingsFeedback = '';
let groupAssignmentEditorState = createGroupAssignmentEditorState();
let loadedSubjectId: string | null = null;
let dashboardRequest: Promise<void> | null = null;
let refreshAvailable = true;
let authorizationProfile: AuthorizationProfile | null = null;
let authorizationRequest: Promise<void> | null = null;
let platformAdministrationModel: PlatformAdministrationModel | undefined;
let platformAdministrationFeedback = '';
let clientDirectory: ClientDirectory | undefined;
let clientFeedback = '';
let clientRequest: Promise<void> | null = null;
let onboardingConfiguration: ClientOnboardingConfiguration | undefined;
let onboardingClientId: string | null = null;
let newlyIssuedOnboardingUrl: string | null = null;
let onboardingRequest: Promise<void> | null = null;
let onboardingRequests: readonly OnboardingRequestListItem[] | undefined;
let onboardingRequestDetail: OnboardingRequestDetail | null = null;
let onboardingFeedback = '';
let activeRoute = currentRoute();

authProvider.subscribe((state) => {
  if (state.status !== 'AUTHENTICATED') {
    loadedSubjectId = null;
    dashboardModel = createDashboardPendingModel('Waiting for authenticated local data');
    settingsModel = undefined;
    settingsFeedback = '';
    groupAssignmentEditorState = createGroupAssignmentEditorState();
    authorizationProfile = null;
    platformAdministrationModel = undefined;
    platformAdministrationFeedback = '';
    clientDirectory = undefined;
    clientFeedback = '';
    onboardingConfiguration = undefined;
    onboardingClientId = null;
    newlyIssuedOnboardingUrl = null;
    onboardingRequests = undefined;
    onboardingRequestDetail = null;
    onboardingFeedback = '';
  }
  render();
  if (state.status === 'AUTHENTICATED' && loadedSubjectId !== state.user.subjectId) {
    void loadAuthenticatedExperience();
  }
});
window.addEventListener('hashchange', (event) => {
  const oldUrl = new URL(event.oldURL);
  handleRouteChange(parseFrontendRoute(`${oldUrl.pathname}${oldUrl.hash}`));
});
window.addEventListener('popstate', () => handleRouteChange(activeRoute));
root.addEventListener('click', (event) => {
  const link = (event.target as Element | null)?.closest<HTMLAnchorElement>('a[data-app-route]');
  if (link == null || link.origin !== window.location.origin) return;
  event.preventDefault();
  const previousRoute = currentRoute();
  window.history.pushState(null, '', `${link.pathname}${link.search}${link.hash}`);
  handleRouteChange(previousRoute);
});

function handleRouteChange(previousRoute = currentRoute()): void {
  const nextRoute = currentRoute();
  if (nextRoute.view !== 'CLIENT_DETAIL' || nextRoute.clientSection !== 'onboarding'
    || nextRoute.clientId !== onboardingClientId) newlyIssuedOnboardingUrl = null;
  activeRoute = nextRoute;
  if (nextRoute.view === 'ONBOARDING'
    && (previousRoute.view !== 'ONBOARDING'
      || previousRoute.onboardingRequestNumber !== nextRoute.onboardingRequestNumber)) {
    onboardingRequestDetail = null;
  }
  const state = authProvider.getState();
  if (isSettingsSectionTransition(previousRoute, nextRoute)
    && state.status === 'AUTHENTICATED'
    && can(effectiveUser(state.user).role, 'MANAGE_OPERATIONAL_SETTINGS')
    && settingsModel !== undefined) {
    updateSettingsSectionNavigation(nextRoute.settingsSection);
    return;
  }
  render();
  if (nextRoute.view === 'SETTINGS' && state.status === 'AUTHENTICATED'
    && can(effectiveUser(state.user).role, 'MANAGE_OPERATIONAL_SETTINGS')
    && settingsModel === undefined) {
    void loadSettings();
  }
  if (nextRoute.view === 'PLATFORM_ADMIN' && state.status === 'AUTHENTICATED'
    && can(effectiveUser(state.user).role, 'ACCESS_PLATFORM_ADMIN')
    && platformAdministrationModel === undefined) {
    void loadPlatformAdministration();
  }
  if ((nextRoute.view === 'CLIENTS' || nextRoute.view === 'CLIENT_DETAIL')
    && state.status === 'AUTHENTICATED' && clientDirectory === undefined) {
    void loadClients();
  }
  if (nextRoute.view === 'CLIENT_DETAIL' && nextRoute.clientSection === 'onboarding'
    && nextRoute.clientId && state.status === 'AUTHENTICATED') {
    void loadClientOnboarding(nextRoute.clientId);
  }
  if (nextRoute.view === 'ONBOARDING' && state.status === 'AUTHENTICATED') {
    void loadOnboardingOperations(nextRoute.onboardingRequestNumber);
  }
}
render();
void authProvider.initialize();

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') void authProvider.refreshSession();
});

function render(): void {
  const route = currentRoute();
  root!.innerHTML = renderApplication(
    effectiveAuthState(),
    dashboardModel,
    settingsModel,
    route.view,
    route.settingsSection,
    platformAdministrationModel,
    platformAdministrationFeedback,
    clientDirectory,
    route.clientId ?? null,
    clientFeedback,
    route.clientSection ?? null,
    route.clientId === onboardingClientId ? onboardingConfiguration : undefined,
    route.clientId === onboardingClientId ? newlyIssuedOnboardingUrl : null,
    onboardingRequests,
    route.view === 'ONBOARDING' ? onboardingRequestDetail : null,
    onboardingFeedback,
  );
  root!.querySelector<HTMLButtonElement>('[data-sign-in]')?.addEventListener('click', () => {
    void authProvider.signIn();
  });
  root!.querySelector<HTMLButtonElement>('[data-sign-out]')?.addEventListener('click', () => {
    void authProvider.signOut();
  });
  root!.querySelector<HTMLButtonElement>('[data-account-toggle]')?.addEventListener('click', (event) => {
    const trigger = event.currentTarget as HTMLButtonElement;
    const menu = root!.querySelector<HTMLElement>('[data-account-menu]');
    if (menu === null) return;
    const opening = menu.hidden;
    menu.hidden = !opening;
    trigger.setAttribute('aria-expanded', String(opening));
  });
  bindSettingsForms();
  bindPlatformAdministrationForms();
  bindClientForms();
  updateSettingsSectionNavigation(route.settingsSection);
  const feedback = root!.querySelector<HTMLElement>('[data-settings-feedback]');
  if (feedback !== null) feedback.textContent = settingsFeedback;
  const refreshButton = root!.querySelector<HTMLButtonElement>('[data-refresh-ninja]');
  if (refreshButton && !refreshAvailable) {
    refreshButton.disabled = true;
    refreshButton.title = 'Local baseline review: restart with dedicated NinjaOne credentials to collect again';
  }
  refreshButton?.addEventListener('click', () => {
    const state = authProvider.getState();
    if (state.status === 'AUTHENTICATED'
      && can(effectiveUser(state.user).role, 'REFRESH_BACKUP_DATA')) {
      void loadDashboard(true);
    }
  });
}

async function loadAuthenticatedExperience(): Promise<void> {
  if (authorizationRequest !== null) return await authorizationRequest;
  const request = (async () => {
    const state = authProvider.getState();
    if (state.status !== 'AUTHENTICATED') return;
    const identityToken = await authProvider.getIdentityToken();
    if (identityToken === null) return;
    try {
      const profile = await authorizationProfileProvider.load(identityToken);
      if (
        profile.subjectId.toLowerCase() !== state.user.subjectId.toLowerCase()
        || profile.tenantId.toLowerCase() !== state.user.tenantId.toLowerCase()
      ) {
        throw new Error('Authorization profile identity mismatch');
      }
      authorizationProfile = profile;
    } catch {
      authorizationProfile = null;
    }
    if (isLandingLocation()) {
      window.history.replaceState(null, '', defaultRoutePath(
        authorizationProfile?.defaultRoute ?? '/overview',
      ));
    }
    await loadDashboard(false);
    await loadClients();
    if (currentRoute().view === 'ONBOARDING') {
      await loadOnboardingOperations(currentRoute().onboardingRequestNumber);
    }
    const route = currentRoute();
    if (route.view === 'CLIENT_DETAIL' && route.clientSection === 'onboarding' && route.clientId) {
      await loadClientOnboarding(route.clientId);
    }
    if (currentRoute().view === 'PLATFORM_ADMIN'
      && can(effectiveUser(state.user).role, 'ACCESS_PLATFORM_ADMIN')) {
      await loadPlatformAdministration();
    }
  })();
  authorizationRequest = request;
  try {
    await request;
  } finally {
    if (authorizationRequest === request) authorizationRequest = null;
  }
}

async function loadClients(): Promise<void> {
  if (clientRequest !== null) return await clientRequest;
  const request = (async () => {
    const state = authProvider.getState();
    if (state.status !== 'AUTHENTICATED') return;
    const identityToken = await authProvider.getIdentityToken();
    if (identityToken === null) return;
    try {
      clientDirectory = await clientProvider.load(identityToken);
      clientFeedback = '';
    } catch {
      clientFeedback = 'Client directory could not be loaded.';
    }
    render();
  })();
  clientRequest = request;
  try {
    await request;
  } finally {
    if (clientRequest === request) clientRequest = null;
  }
}

async function loadOnboardingOperations(requestNumber?: string): Promise<void> {
  const state = authProvider.getState();
  if (state.status !== 'AUTHENTICATED') return;
  const identityToken = await authProvider.getIdentityToken();
  if (identityToken === null) return;
  try {
    const requests = await onboardingOperationsProvider.list(identityToken);
    const detail = requestNumber
      ? await onboardingOperationsProvider.detail(identityToken, requestNumber) : null;
    if (currentRoute().view !== 'ONBOARDING'
      || currentRoute().onboardingRequestNumber !== requestNumber) return;
    onboardingRequests = requests;
    onboardingRequestDetail = detail;
    onboardingFeedback = '';
  } catch {
    onboardingRequestDetail = null;
    onboardingFeedback = 'Onboarding requests could not be loaded.';
  }
  if (currentRoute().view === 'ONBOARDING') render();
}

function bindClientForms(): void {
  bindClientOnboardingForms();
  const newClientForm = root!.querySelector<HTMLFormElement>('[data-new-client-form]');
  root!.querySelector<HTMLButtonElement>('[data-new-client-toggle]')?.addEventListener('click', () => {
    if (newClientForm === null) return;
    newClientForm.hidden = !newClientForm.hidden;
    if (!newClientForm.hidden) newClientForm.querySelector<HTMLInputElement>('[name="name"]')?.focus();
  });
  if (newClientForm !== null) {
    const name = newClientForm.querySelector<HTMLInputElement>('[name="name"]');
    const slug = newClientForm.querySelector<HTMLInputElement>('[name="slug"]');
    let slugEdited = false;
    slug?.addEventListener('input', () => { slugEdited = true; });
    name?.addEventListener('input', () => {
      if (slug !== null && !slugEdited) slug.value = slugFromName(name.value);
    });
    newClientForm.addEventListener('submit', (event) => {
      event.preventDefault();
      const data = new FormData(newClientForm);
      const input: CreateClientInput = {
        name: formText(data, 'name'),
        slug: formText(data, 'slug'),
        active: data.has('active'),
      };
      void mutateClients((token) => clientProvider.create(token, input), true);
    });
  }
  root!.querySelector<HTMLInputElement>('[data-client-search]')?.addEventListener('input', (event) => {
    const needle = (event.currentTarget as HTMLInputElement).value.trim().toLowerCase();
    let visible = 0;
    root!.querySelectorAll<HTMLElement>('[data-client-search-name]').forEach((card) => {
      card.hidden = !(card.dataset.clientSearchName ?? '').includes(needle);
      if (!card.hidden) visible++;
    });
    const empty = root!.querySelector<HTMLElement>('[data-client-search-empty]');
    if (empty !== null) empty.hidden = visible !== 0;
  });
  root!.querySelectorAll<HTMLButtonElement>('[data-create-from-ninja]').forEach((button) => {
    button.addEventListener('click', () => {
      const organizationId = Number(button.dataset.createFromNinja);
      void mutateClients((token) => clientProvider.createFromNinja(token, organizationId), true);
    });
  });
  root!.querySelectorAll<HTMLFormElement>('[data-link-ninja-form]').forEach((form) => {
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const clientId = formText(new FormData(form), 'clientId');
      const organizationId = Number(form.dataset.organizationId);
      void mutateClients((token) => clientProvider.linkNinja(token, clientId, organizationId), false);
    });
  });
}

async function loadClientOnboarding(clientId: string): Promise<void> {
  if (onboardingClientId === clientId && onboardingConfiguration !== undefined) return;
  if (onboardingRequest !== null) {
    await onboardingRequest;
    if (onboardingClientId === clientId && onboardingConfiguration !== undefined) return;
  }
  const request = (async () => {
    const state = authProvider.getState();
    if (state.status !== 'AUTHENTICATED') return;
    const identityToken = await authProvider.getIdentityToken();
    if (identityToken === null) return;
    try {
      const configuration = await onboardingProvider.load(identityToken, clientId);
      onboardingConfiguration = configuration;
      onboardingClientId = clientId;
      clientFeedback = '';
    } catch {
      clientFeedback = 'Client onboarding configuration could not be loaded.';
    }
    render();
  })();
  onboardingRequest = request;
  try { await request; }
  finally { if (onboardingRequest === request) onboardingRequest = null; }
}

async function mutateClientOnboarding(
  operation: (identityToken: string, clientId: string) => Promise<ClientOnboardingConfiguration>,
  message: string,
  form?: HTMLFormElement,
): Promise<void> {
  const state = authProvider.getState();
  const route = currentRoute();
  const clientId = route.clientId;
  if (state.status !== 'AUTHENTICATED' || !clientId
    || !can(effectiveUser(state.user).roles, 'MANAGE_CLIENTS')) return;
  const identityToken = await authProvider.getIdentityToken();
  if (identityToken === null) return;
  try {
    form?.querySelector('[data-onboarding-form-error]')?.remove();
    onboardingConfiguration = await operation(identityToken, clientId);
    onboardingClientId = clientId;
    newlyIssuedOnboardingUrl = null;
    clientFeedback = message;
  } catch (error) {
    clientFeedback = error instanceof ClientOnboardingRequestError ? error.message : 'Onboarding configuration could not be saved.';
    if (form?.isConnected) {
      showOnboardingFormError(form, clientFeedback);
      return;
    }
  }
  render();
}

function showOnboardingFormError(form: HTMLFormElement, message: string): void {
  let feedback = form.querySelector<HTMLElement>('[data-onboarding-form-error]');
  if (feedback === null) {
    feedback = document.createElement('p');
    feedback.className = 'client-feedback';
    feedback.dataset.onboardingFormError = '';
    feedback.setAttribute('role', 'alert');
    const actions = form.querySelector('.onboarding-profile-save');
    if (actions) actions.before(feedback);
    else form.prepend(feedback);
  }
  feedback.textContent = message;
}

function profileFieldError(input: HTMLInputElement): string | null {
  if (input.name === 'name') return input.value.trim() ? null : 'Profile name is required.';
  if (input.hasAttribute('data-item-name')) return input.value.trim() ? null : 'Item name is required.';
  if (input.hasAttribute('data-item-quantity')) {
    const count = Number(input.value);
    return input.value.trim() && Number.isInteger(count) && count >= 1 && count <= 100
      ? null : 'Enter a whole number from 1 to 100.';
  }
  if (input.name === 'monitorCount') {
    if (input.disabled) return null;
    const count = Number(input.value);
    return input.value.trim() && Number.isInteger(count) && count >= 1 && count <= 8
      ? null : 'Enter a whole number from 1 to 8.';
  }
  return null;
}

function setProfileFieldError(input: HTMLInputElement, message: string | null): void {
  const label = input.closest('label');
  if (message === null) {
    input.removeAttribute('aria-invalid');
    label?.querySelector('[data-onboarding-field-error]')?.remove();
    return;
  }
  input.setAttribute('aria-invalid', 'true');
  let hint = label?.querySelector<HTMLElement>('[data-onboarding-field-error]');
  if (label && hint === null) {
    hint = document.createElement('small');
    hint.className = 'onboarding-field-error';
    hint.dataset.onboardingFieldError = '';
    label.append(hint);
  }
  if (hint) hint.textContent = message;
}

function validateProfileFields(form: HTMLFormElement): boolean {
  let firstInvalid: HTMLInputElement | null = null;
  for (const input of form.querySelectorAll<HTMLInputElement>(
    'input[name="name"], input[data-item-name], input[data-item-quantity], input[name="monitorCount"]',
  )) {
    const error = profileFieldError(input);
    setProfileFieldError(input, error);
    if (error !== null && firstInvalid === null) firstInvalid = input;
  }
  if (firstInvalid === null) return true;
  showOnboardingFormError(form, 'Complete the highlighted fields before saving.');
  firstInvalid.closest('details')?.setAttribute('open', '');
  firstInvalid.focus();
  return false;
}

function bindClientOnboardingForms(): void {
  root!.querySelectorAll<HTMLButtonElement>('[data-onboarding-token]').forEach((button) => {
    button.addEventListener('click', () => {
      const action = button.dataset.onboardingToken;
      if (action === 'rotate' && !window.confirm('Rotate this public link? The previous link will stop working immediately.')) return;
      if (action === 'create' || action === 'rotate') {
        void issueOnboardingToken(action === 'rotate');
      } else if (action === 'activate' || action === 'deactivate') {
        void mutateClientOnboarding(
          (token, clientId) => onboardingProvider.setTokenActive(token, clientId, action === 'activate'),
          action === 'activate' ? 'Public link activated.' : 'Public link deactivated.',
        );
      }
    });
  });
  root!.querySelector<HTMLButtonElement>('[data-copy-onboarding-url]')?.addEventListener('click', () => {
    const input = root!.querySelector<HTMLInputElement>('[data-new-onboarding-url]');
    if (input) void navigator.clipboard.writeText(input.value);
  });
  root!.querySelectorAll<HTMLFormElement>('[data-onboarding-location]').forEach((form) => {
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const data = new FormData(form);
      void mutateClientOnboarding((token, clientId) => onboardingProvider.saveLocation(token, clientId, {
        id: form.dataset.id || null, name: formText(data, 'name'),
        address: formText(data, 'address') || null, active: data.has('active'),
      }), 'Location saved.');
    });
  });
  root!.querySelectorAll<HTMLFormElement>('[data-onboarding-department]').forEach((form) => {
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const data = new FormData(form);
      void mutateClientOnboarding((token, clientId) => onboardingProvider.saveDepartment(token, clientId, {
        id: form.dataset.id || null, name: formText(data, 'name'), active: data.has('active'),
      }), 'Department saved.');
    });
  });
  root!.querySelectorAll<HTMLFormElement>('[data-onboarding-profile]').forEach((form) => {
    form.addEventListener('input', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.getAttribute('aria-invalid') !== 'true') return;
      setProfileFieldError(input, profileFieldError(input));
      if (!form.querySelector('[aria-invalid="true"]')) {
        form.querySelector('[data-onboarding-form-error]')?.remove();
      }
    });
    const printerChecks = Array.from(form.querySelectorAll<HTMLInputElement>('[name="printerAccessRequired"]'));
    printerChecks.forEach((input) => input.addEventListener('change', () => {
      const section = form.querySelector<HTMLElement>('[data-onboarding-printers]');
      if (section) section.hidden = !printerChecks.some((check) => check.checked);
    }));
    const monitor = form.querySelector<HTMLInputElement>('[name="monitorEnabled"]');
    monitor?.addEventListener('change', () => {
      const quantity = form.querySelector<HTMLElement>('[data-monitor-quantity]');
      const input = form.querySelector<HTMLInputElement>('[name="monitorCount"]');
      if (quantity) quantity.hidden = !monitor.checked;
      if (input) { input.disabled = !monitor.checked; setProfileFieldError(input, null); }
    });
    form.querySelectorAll<HTMLButtonElement>('[data-add-onboarding-row]').forEach((button) => {
      button.addEventListener('click', () => {
        const kind = button.dataset.addOnboardingRow;
        if (!kind) return;
        const list = form.querySelector<HTMLElement>(`[data-onboarding-list="${kind}"]`);
        if (list) list.insertAdjacentHTML('beforeend', blankOnboardingRow(kind));
      });
    });
    form.addEventListener('click', (event) => {
      const button = (event.target as Element | null)?.closest<HTMLButtonElement>('[data-remove-onboarding-row]');
      button?.closest('[data-onboarding-row]')?.remove();
    });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!validateProfileFields(form)) return;
      const data = new FormData(form);
      const bool = (name: string): boolean => data.has(name);
      const optional = (name: string): string | null => formText(data, name).trim() || null;
      const items = (kind: string): HTMLElement[] => Array.from(form.querySelectorAll<HTMLElement>(`[data-onboarding-row="${kind}"]`));
      const itemText = (row: HTMLElement, selector: string): string => row.querySelector<HTMLInputElement>(selector)?.value.trim() ?? '';
      const itemOptional = (row: HTMLElement, selector: string): string | null => itemText(row, selector) || null;
      const input: ProfileInput = {
        id: form.dataset.id || null, name: formText(data, 'name'), description: optional('description'), active: bool('active'),
        access: {
          ...EMPTY_ACCESS,
          microsoft365Required: bool('microsoft365Required'),
          officeAppsRequired: bool('officeAppsRequired'), teamsRequired: bool('teamsRequired'),
          vpnRequired: bool('vpnRequired'), phoneRequired: bool('phoneRequired'),
          printerAccessRequired: form.querySelector<HTMLInputElement>('[data-onboarding-access-defaults] [name="printerAccessRequired"]')?.checked ?? false, notes: optional('accessNotes'),
        },
        printers: items('printers').map((row) => ({
          id: row.dataset.itemId || null, name: itemText(row, '[data-item-name]'), active: true,
        })),
        customAccess: items('customAccess').map((row) => ({
          id: row.dataset.itemId || null, name: itemText(row, '[data-item-name]'),
          notes: itemOptional(row, '[data-item-notes]'),
          active: row.querySelector<HTMLInputElement>('[name="itemActive"]')?.checked ?? false,
        })),
        software: items('software').map((row) => ({
          id: row.dataset.itemId || null, softwareName: itemText(row, '[data-item-name]'),
          systemDetail: itemOptional(row, '[data-item-extra]'),
          installRequired: row.querySelector<HTMLInputElement>('[name="itemRequired"]')?.checked ?? false,
          licenseRequired: row.querySelector<HTMLInputElement>('[name="itemSecond"]')?.checked ?? false,
          notes: itemOptional(row, '[data-item-notes]'),
        })),
        sharedDrives: items('sharedDrives').map((row) => ({
          id: row.dataset.itemId || null, driveName: itemText(row, '[data-item-name]'),
          drivePath: itemOptional(row, '[data-item-extra]'),
        })),
        groups: items('groups').map((row) => ({
          id: row.dataset.itemId || null, groupName: itemText(row, '[data-item-name]'),
          systemName: itemOptional(row, '[data-item-extra]'), groupType: itemOptional(row, '[data-item-detail]'),
          notes: itemOptional(row, '[data-item-notes]'),
        })),
        hardware: {
          ...DEFAULT_HARDWARE,
          desktopRequired: bool('desktopRequired'), laptopRequired: bool('laptopRequired'),
          dockingStationRequired: bool('dockingStationRequired'),
          monitorCount: bool('monitorEnabled') ? Number(formText(data, 'monitorCount')) : 0,
          keyboardMouseRequired: bool('keyboardMouseRequired'), headsetRequired: bool('headsetRequired'),
          deskPhoneRequired: bool('deskPhoneRequired'), scannerRequired: bool('scannerRequired'),
          printerAccessRequired: form.querySelector<HTMLInputElement>('[data-onboarding-hardware-defaults] [name="printerAccessRequired"]')?.checked ?? false, notes: optional('hardwareNotes'),
        },
        customHardware: items('customHardware').map((row) => ({
          id: row.dataset.itemId || null, name: itemText(row, '[data-item-name]'),
          quantity: Number(itemText(row, '[data-item-quantity]')),
          notes: itemOptional(row, '[data-item-notes]'),
          active: row.querySelector<HTMLInputElement>('[name="itemActive"]')?.checked ?? false,
        })),
      };
      const validationError = profileValidationError(input);
      if (validationError !== null) {
        showOnboardingFormError(form, validationError);
        return;
      }
      void mutateClientOnboarding((token, clientId) => onboardingProvider.saveProfile(token, clientId, input), 'Profile saved.', form);
    });
  });
}

async function issueOnboardingToken(rotate: boolean): Promise<void> {
  const state = authProvider.getState();
  const route = currentRoute();
  const clientId = route.clientId;
  if (state.status !== 'AUTHENTICATED' || !clientId
    || !can(effectiveUser(state.user).roles, 'MANAGE_CLIENTS')) return;
  const identityToken = await authProvider.getIdentityToken();
  if (identityToken === null) return;
  try {
    const issued = await onboardingProvider.issueToken(identityToken, clientId, rotate);
    newlyIssuedOnboardingUrl = `${window.location.origin}/o/${issued.rawToken}`;
    if (onboardingClientId === clientId && onboardingConfiguration !== undefined) {
      onboardingConfiguration = {
        ...onboardingConfiguration, tokenConfigured: true, tokenActive: true,
        tokenLastRotatedAt: issued.tokenLastRotatedAt,
      };
    }
    onboardingClientId = clientId;
    try { onboardingConfiguration = await onboardingProvider.load(identityToken, clientId); }
    catch { /* Keep the one-time URL visible even if the follow-up read is unavailable. */ }
    clientFeedback = 'Public link issued. Copy it now; the token cannot be retrieved later.';
  } catch (error) {
    clientFeedback = error instanceof ClientOnboardingRequestError ? error.message : 'Public link could not be issued.';
  }
  render();
}

async function mutateClients(
  operation: (token: string) => Promise<{ id: string }>,
  openDetail: boolean,
): Promise<void> {
  const state = authProvider.getState();
  if (state.status !== 'AUTHENTICATED' || !can(effectiveUser(state.user).roles, 'MANAGE_CLIENTS')) return;
  const identityToken = await authProvider.getIdentityToken();
  if (identityToken === null) return;
  try {
    const client = await operation(identityToken);
    clientDirectory = await clientProvider.load(identityToken);
    clientFeedback = openDetail ? 'Client created.' : 'NinjaOne organization linked.';
    if (openDetail) window.history.pushState(null, '', `/clients/${encodeURIComponent(client.id)}`);
  } catch (error) {
    clientFeedback = error instanceof ClientRequestError ? error.message : 'Client change could not be saved.';
  }
  render();
}

async function loadDashboard(refreshFromNinja: boolean): Promise<void> {
  if (dashboardRequest !== null) return await dashboardRequest;
  const request = (async () => {
    const state = authProvider.getState();
    if (state.status !== 'AUTHENTICATED') return;
    const user = effectiveUser(state.user);
    if (refreshFromNinja && !can(user.role, 'REFRESH_BACKUP_DATA')) return;
    dashboardModel = createDashboardPendingModel(
      refreshFromNinja ? 'Refreshing NinjaOne backup baseline' : 'Loading local backup baseline',
    );
    render();
    const identityToken = await authProvider.getIdentityToken();
    if (identityToken === null) return;
    try {
      const payload = refreshFromNinja
        ? await dashboardProvider.refreshFromNinja(identityToken)
        : await dashboardProvider.load(identityToken);
      dashboardModel = payload.dashboard;
      refreshAvailable = payload.refreshAvailable !== false;
      loadedSubjectId = state.user.subjectId;
      if (refreshFromNinja) await loadClients();
      if (can(user.role, 'MANAGE_OPERATIONAL_SETTINGS')) {
        try {
          settingsModel = await settingsProvider.load(identityToken);
        } catch {
          settingsFeedback = 'Persisted settings could not be loaded.';
        }
      }
    } catch {
      dashboardModel = createDashboardPendingModel('Local backup data could not be loaded');
    }
    render();
  })();
  dashboardRequest = request;
  try {
    await request;
  } finally {
    if (dashboardRequest === request) dashboardRequest = null;
  }
}

async function loadSettings(): Promise<void> {
  const state = authProvider.getState();
  if (state.status !== 'AUTHENTICATED'
    || !can(effectiveUser(state.user).role, 'MANAGE_OPERATIONAL_SETTINGS')) return;
  const identityToken = await authProvider.getIdentityToken();
  if (identityToken === null) return;
  try {
    settingsModel = await settingsProvider.load(identityToken);
    settingsFeedback = '';
  } catch {
    settingsFeedback = 'Persisted settings could not be loaded.';
  }
  render();
}

async function loadPlatformAdministration(): Promise<void> {
  const state = authProvider.getState();
  if (state.status !== 'AUTHENTICATED'
    || !can(effectiveUser(state.user).role, 'ACCESS_PLATFORM_ADMIN')) return;
  const identityToken = await authProvider.getIdentityToken();
  if (identityToken === null) return;
  try {
    platformAdministrationModel = await platformAdministrationProvider.load(identityToken);
    platformAdministrationFeedback = '';
  } catch {
    platformAdministrationFeedback = 'Application users and profiles could not be loaded.';
  }
  render();
}

function bindSettingsForms(): void {
  root!.querySelector<HTMLFormElement>('[data-email-delivery-form]')?.addEventListener('submit', (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget as HTMLFormElement);
    void mutateSettings(
      (token) => settingsProvider.updateEmailDelivery(token, {
        senderDisplayName: formText(data, 'senderDisplayName'),
        senderMailbox: formText(data, 'senderMailbox'),
      }),
      'Email delivery settings saved. Sender verification is required after a mailbox change.',
    );
  });
  root!.querySelector<HTMLButtonElement>('[data-test-email]')?.addEventListener('click', (event) => {
    const button = event.currentTarget as HTMLButtonElement;
    const input = root!.querySelector<HTMLInputElement>('[data-test-email-recipient]');
    if (input === null || !input.reportValidity()) return;
    button.disabled = true;
    button.textContent = 'Testing…';
    void testEmailDelivery(input.value);
  });
  root!.querySelector<HTMLButtonElement>('[data-add-primary-recipient]')?.addEventListener('click', () => {
    const list = root!.querySelector<HTMLElement>('[data-primary-recipient-list]');
    if (list === null) return;
    const row = document.createElement('label');
    row.className = 'recipient-input-row';
    const input = document.createElement('input');
    input.type = 'email';
    input.name = 'recipient';
    input.required = true;
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'table-action danger';
    remove.dataset.removePrimaryRecipient = '';
    remove.textContent = 'Remove';
    remove.addEventListener('click', () => row.remove());
    row.append(input, remove);
    list.append(row);
    input.focus();
  });
  root!.querySelectorAll<HTMLButtonElement>('[data-remove-primary-recipient]').forEach((button) => {
    button.addEventListener('click', () => button.closest('.recipient-input-row')?.remove());
  });
  root!.querySelector<HTMLFormElement>('[data-primary-recipients-form]')?.addEventListener('submit', (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget as HTMLFormElement);
    const recipients = data.getAll('recipient').flatMap((value) =>
      typeof value === 'string' && value.trim() !== '' ? [value] : []);
    void mutateSettings(
      (token) => settingsProvider.replacePrimaryRecipients(token, recipients),
      'Primary alert recipients saved.',
    );
  });
  root!.querySelectorAll<HTMLFormElement>('[data-group-form]').forEach((form) => {
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const data = new FormData(form);
      const input: NotificationGroupInput = {
        name: formText(data, 'name'),
        description: formText(data, 'description'),
        recipients: splitRecipients(formText(data, 'recipients')),
      };
      const id = form.dataset.groupId ?? '';
      void mutateSettings(
        (token) => id === ''
          ? settingsProvider.createGroup(token, input)
          : settingsProvider.updateGroup(token, id, input),
        id === '' ? 'Notification group created.' : 'Notification group saved.',
      );
    });
  });
  root!.querySelectorAll<HTMLButtonElement>('[data-delete-group]').forEach((button) => {
    button.addEventListener('click', () => {
      const id = button.dataset.deleteGroup;
      if (id === undefined || !window.confirm('Delete this notification group?')) return;
      void mutateSettings(
        (token) => settingsProvider.deleteGroup(token, id),
        'Notification group deleted.',
      );
    });
  });
  bindGroupAssignmentEditor();
}

function bindGroupAssignmentEditor(): void {
  const form = root!.querySelector<HTMLFormElement>('[data-group-assignment-form]');
  const model = settingsModel;
  if (form === null || model === undefined) return;
  const groupSelect = form.querySelector<HTMLSelectElement>('[data-group-assignment-group]');
  const search = form.querySelector<HTMLInputElement>('[data-group-assignment-search]');
  if (groupSelect === null || search === null) return;

  if (groupAssignmentEditorState.groupId !== null) {
    const groupExists = model.additionalNotificationGroups.some(
      (group) => group.groupId === groupAssignmentEditorState.groupId,
    );
    if (!groupExists) groupAssignmentEditorState = createGroupAssignmentEditorState();
    else if (!hasUnsavedGroupAssignmentChanges(groupAssignmentEditorState)) {
      groupAssignmentEditorState = loadGroupAssignment(
        model,
        groupAssignmentEditorState.groupId,
        groupAssignmentEditorState.search,
      );
    }
  }
  groupSelect.value = groupAssignmentEditorState.groupId ?? '';
  search.value = groupAssignmentEditorState.search;

  groupSelect.addEventListener('change', () => {
    const nextGroupId = groupSelect.value;
    if (!allowGroupAssignmentSwitch(
      groupAssignmentEditorState,
      () => window.confirm('Discard unsaved endpoint assignment changes?'),
    )) {
      groupSelect.value = groupAssignmentEditorState.groupId ?? '';
      return;
    }
    groupAssignmentEditorState = nextGroupId === ''
      ? createGroupAssignmentEditorState()
      : loadGroupAssignment(model, nextGroupId);
    synchronizeGroupAssignmentEditor(model, form);
  });
  search.addEventListener('input', () => {
    groupAssignmentEditorState = withAssignmentSearch(
      groupAssignmentEditorState,
      search.value,
    );
    synchronizeGroupAssignmentEditor(model, form);
  });
  form.querySelectorAll<HTMLInputElement>('input[name="endpoint"]').forEach((checkbox) => {
    checkbox.addEventListener('change', () => {
      groupAssignmentEditorState = toggleAssignmentEndpoint(
        groupAssignmentEditorState,
        checkbox.value,
        checkbox.checked,
      );
      synchronizeGroupAssignmentEditor(model, form);
    });
  });
  form.querySelector<HTMLButtonElement>('[data-select-all-filtered]')?.addEventListener('click', () => {
    groupAssignmentEditorState = selectAllFilteredEndpoints(
      groupAssignmentEditorState,
      filteredAssignmentEndpoints(model, groupAssignmentEditorState.search),
    );
    synchronizeGroupAssignmentEditor(model, form);
  });
  form.querySelector<HTMLButtonElement>('[data-clear-group-assignment]')?.addEventListener('click', () => {
    groupAssignmentEditorState = clearAssignmentSelection(groupAssignmentEditorState);
    synchronizeGroupAssignmentEditor(model, form);
  });
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void saveGroupAssignment(model, form);
  });
  synchronizeGroupAssignmentEditor(model, form);
}

function synchronizeGroupAssignmentEditor(
  model: SettingsPageModel,
  form: HTMLFormElement,
): void {
  const enabled = groupAssignmentEditorState.groupId !== null;
  const visible = new Set(
    filteredAssignmentEndpoints(model, groupAssignmentEditorState.search)
      .map((endpoint) => `${endpoint.organizationId}:${endpoint.deviceId}`),
  );
  form.querySelector<HTMLElement>('[data-group-assignment-controls]')
    ?.setAttribute('aria-disabled', String(!enabled));
  const instruction = form.querySelector<HTMLElement>('[data-group-assignment-instruction]');
  if (instruction !== null) instruction.hidden = enabled;
  form.querySelectorAll<HTMLElement>('[data-group-assignment-endpoint]').forEach((row) => {
    const key = row.dataset.groupAssignmentEndpoint ?? '';
    row.hidden = !visible.has(key);
    const checkbox = row.querySelector<HTMLInputElement>('input[name="endpoint"]');
    if (checkbox !== null) {
      checkbox.disabled = !enabled;
      checkbox.checked = groupAssignmentEditorState.selectedEndpointKeys.has(key);
    }
  });
  const search = form.querySelector<HTMLInputElement>('[data-group-assignment-search]');
  if (search !== null) search.disabled = !enabled;
  const noResults = form.querySelector<HTMLElement>('[data-group-assignment-empty]');
  if (noResults !== null) noResults.hidden = !enabled || visible.size > 0;
  const selectAll = form.querySelector<HTMLButtonElement>('[data-select-all-filtered]');
  if (selectAll !== null) selectAll.disabled = !enabled || visible.size === 0;
  const clear = form.querySelector<HTMLButtonElement>('[data-clear-group-assignment]');
  if (clear !== null) {
    clear.disabled = !enabled || groupAssignmentEditorState.selectedEndpointKeys.size === 0;
  }
  const count = groupAssignmentEditorState.selectedEndpointKeys.size;
  const countLabel = form.querySelector<HTMLElement>('[data-group-assignment-count]');
  if (countLabel !== null) {
    countLabel.textContent = `${count} endpoint${count === 1 ? '' : 's'} selected`;
  }
  const save = form.querySelector<HTMLButtonElement>('[data-save-group-assignment]');
  if (save !== null) save.disabled = !enabled;
}

async function saveGroupAssignment(
  model: SettingsPageModel,
  form: HTMLFormElement,
): Promise<void> {
  const groupId = groupAssignmentEditorState.groupId;
  if (groupId === null) return;
  const state = authProvider.getState();
  if (state.status !== 'AUTHENTICATED'
    || !can(effectiveUser(state.user).role, 'MANAGE_OPERATIONAL_SETTINGS')) return;
  const desiredEndpoints = selectedAssignmentEndpoints(groupAssignmentEditorState);
  setGroupAssignmentEditorBusy(form, true);
  try {
    const identityToken = await authProvider.getIdentityToken();
    if (identityToken === null) throw new Error('Identity token is unavailable');
    const updated = await settingsProvider.replaceGroupAssignments(
      identityToken,
      groupId,
      desiredEndpoints,
    );
    settingsModel = updated;
    const groupName = model.additionalNotificationGroups.find(
      (group) => group.groupId === groupId,
    )?.groupName ?? 'additional group';
    groupAssignmentEditorState = loadGroupAssignment(
      updated,
      groupId,
      groupAssignmentEditorState.search,
    );
    settingsFeedback = `${desiredEndpoints.length} endpoint${desiredEndpoints.length === 1 ? '' : 's'} assigned to ${groupName}.`;
    render();
  } catch (error) {
    settingsFeedback = error instanceof SettingsRequestError
      ? error.message
      : 'Group assignments could not be saved.';
    const feedback = root!.querySelector<HTMLElement>('[data-settings-feedback]');
    if (feedback !== null) feedback.textContent = settingsFeedback;
    setGroupAssignmentEditorBusy(form, false);
    synchronizeGroupAssignmentEditor(model, form);
  }
}

function setGroupAssignmentEditorBusy(form: HTMLFormElement, busy: boolean): void {
  form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLButtonElement>(
    'input, select, button',
  ).forEach((control) => {
    control.disabled = busy;
  });
}

function bindPlatformAdministrationForms(): void {
  root!.querySelectorAll<HTMLFormElement>('[data-user-profile-form]').forEach((form) => {
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const subjectId = form.dataset.subjectId ?? '';
      const data = new FormData(form);
      void updateOperationalProfile(subjectId, formText(data, 'operationalProfile'));
    });
  });
}

async function updateOperationalProfile(
  subjectId: string,
  operationalProfile: string,
): Promise<void> {
  const state = authProvider.getState();
  if (state.status !== 'AUTHENTICATED'
    || !can(effectiveUser(state.user).role, 'ACCESS_PLATFORM_ADMIN')) return;
  const identityToken = await authProvider.getIdentityToken();
  if (identityToken === null) return;
  try {
    platformAdministrationModel = await platformAdministrationProvider.updateOperationalProfile(
      identityToken,
      subjectId,
      operationalProfile,
    );
    authorizationProfile = await authorizationProfileProvider.load(identityToken);
    platformAdministrationFeedback = 'Operational profile saved.';
  } catch {
    platformAdministrationFeedback = 'Operational profile could not be saved.';
  }
  render();
}

async function testEmailDelivery(recipient: string): Promise<void> {
  const state = authProvider.getState();
  if (state.status !== 'AUTHENTICATED'
    || !can(effectiveUser(state.user).role, 'TEST_EMAIL_DELIVERY')) return;
  const identityToken = await authProvider.getIdentityToken();
  if (identityToken === null) return;
  try {
    const result = await settingsProvider.testEmailDelivery(identityToken, recipient);
    settingsModel = result.settings;
    settingsFeedback = result.message;
  } catch (error) {
    settingsFeedback = error instanceof SettingsRequestError
      ? error.message
      : 'Test email could not be completed.';
  }
  render();
}

async function mutateSettings(
  operation: (identityToken: string) => Promise<SettingsPageModel>,
  successMessage: string,
): Promise<void> {
  const state = authProvider.getState();
  if (state.status !== 'AUTHENTICATED'
    || !can(effectiveUser(state.user).role, 'MANAGE_OPERATIONAL_SETTINGS')) return;
  const identityToken = await authProvider.getIdentityToken();
  if (identityToken === null) return;
  try {
    settingsModel = await operation(identityToken);
    settingsFeedback = successMessage;
  } catch (error) {
    settingsFeedback = error instanceof SettingsRequestError
      ? error.message
      : 'Settings could not be saved.';
  }
  render();
}

function formText(data: FormData, name: string): string {
  const value = data.get(name);
  return typeof value === 'string' ? value : '';
}

function splitRecipients(value: string): string[] {
  return value.split(/\r?\n|,/).map((recipient) => recipient.trim()).filter(Boolean);
}

function currentRoute(): FrontendRoute {
  return parseFrontendRoute(`${window.location.pathname}${window.location.hash}`);
}

function isLandingLocation(): boolean {
  return (window.location.pathname === '/' || window.location.pathname === '/index.html')
    && (window.location.hash === '' || window.location.hash === '#');
}

function effectiveAuthState(): AuthState {
  const state = authProvider.getState();
  return state.status === 'AUTHENTICATED'
    ? { status: 'AUTHENTICATED', user: effectiveUser(state.user) }
    : state;
}

function effectiveUser(user: AuthenticatedUser): AuthenticatedUser {
  if (authorizationProfile === null) return user;
  return {
    ...user,
    roles: authorizationProfile.roles,
    role: authorizationProfile.role,
    operationalProfile: authorizationProfile.operationalProfile,
  };
}

function updateSettingsSectionNavigation(section: SettingsSectionId | null): void {
  const selected = section ?? 'ninja-backup-inventory';
  root!.querySelectorAll<HTMLAnchorElement>('[data-settings-section]').forEach((link) => {
    const active = link.dataset.settingsSection === selected;
    link.classList.toggle('active', active);
    if (active) link.setAttribute('aria-current', 'location');
    else link.removeAttribute('aria-current');
  });
  if (section === null) return;
  window.requestAnimationFrame(() => {
    root!.querySelector<HTMLElement>(`#${section}`)?.scrollIntoView({ block: 'start' });
  });
}

declare global {
  interface Window {
    __MSP_HEALTH_CONFIG__?: {
      authMode?: unknown;
      entraClientId?: unknown;
      entraTenantId?: unknown;
      entraRedirectUri?: unknown;
      mockRole?: unknown;
    };
  }
}
